import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/layout/PageHeader';
import * as swimmingService from '../services/swimmingService';
import { BRAND_GREEN, BRAND_GOLD, BRAND_GREEN_LIGHT } from '../theme/sucronTheme';
import { useSportsLang } from '../contexts/SportsLangContext';


// === CORES DA MARCA BASEADAS NA LOGO - DASHBOARD STYLE ===
const BRAND_GOLD_LIGHT = 'var(--brand-gold-light)';   // Dourado muito claro para fundos
const BRAND_GREEN_BORDER = 'var(--brand-border)'; // Verde para bordas
const BRAND_GOLD_BORDER = 'var(--brand-border-gold)';  // Dourado para bordas

// === PALETA DE TONS DE VERDE PARA GRÁFICOS ===
const getGreenShade = (index, total) => {
  const greenShades = [
    BRAND_GREEN, // Verde escuro
    '#374151', // Verde médio-escuro
    '#059669', // Verde médio
    '#10b981', // Verde médio-claro
    '#34d399', // Verde claro
    '#6ee7b7', // Verde muito claro
  ];
  
  if (total <= greenShades.length) {
    return greenShades[index % greenShades.length];
  }
  
  // Se tiver mais barras, interpolar tons
  const ratio = index / (total - 1);
  const darkGreen = [6, 95, 70];   // RGB do verde escuro
  const lightGreen = [110, 231, 183]; // RGB do verde claro
  
  const r = Math.round(darkGreen[0] + (lightGreen[0] - darkGreen[0]) * ratio);
  const g = Math.round(darkGreen[1] + (lightGreen[1] - darkGreen[1]) * ratio);
  const b = Math.round(darkGreen[2] + (lightGreen[2] - darkGreen[2]) * ratio);
  
  return `rgb(${r}, ${g}, ${b})`;
};

// === FUNÇÃO PARA FORMATAR NOMES DAS PROVAS ===
const formatEventName = (distance, stroke, gender, isRelay) => {
  // Mapeamento de estilos em inglês para português
  const strokeMap = {
    'Free': 'Livre',
    'Freestyle': 'Livre',
    'Backstroke': 'Costas',
    'Back': 'Costas',
    'Breaststroke': 'Peito',
    'Breast': 'Peito',
    'Butterfly': 'Borboleta',
    'Fly': 'Borboleta',
    'Medley': 'Medley',
    'IM': 'Medley'
  };

  // Mapeamento de gênero
  const genderMap = {
    'M': 'Masculino',
    'F': 'Feminino',
    'Male': 'Masculino',
    'Female': 'Feminino',
    'Masculino': 'Masculino',
    'Feminino': 'Feminino'
  };

  const strokePt = strokeMap[stroke] || stroke;
  const relayText = isRelay ? ' Revezamento' : '';

  return `${distance}m ${strokePt}${relayText}`;
};

// === CONFIGURAÇÃO DE DEBUG ===
const DEBUG = {
  logos: false,        // Logs de carregamento de logos
  competitions: false, // Logs de competições
  events: false,       // Logs de eventos/provas
  results: false,      // Logs de resultados
  clicks: false        // Logs de cliques
};

// === MAPEAMENTO DE LOGOS DOS CLUBES ===
const getClubLogo = (clubName) => {
  if (DEBUG.logos) console.log(' getClubLogo:', clubName);
  
  const clubLogos = {
    'MINAS TÊNIS CLUBE': '/logos-natacao/minas-tenis-clube.png',
    'MINAS TENIS CLUBE': '/logos-natacao/minas-tenis-clube.png',
    'PINHEIROS': '/logos-natacao/pinheiros.png',
    'ESPORTE CLUBE PINHEIROS': '/logos-natacao/pinheiros.png',
    'UNISANTA': '/logo natacao/unisanta.png',
    'CORINTHIANS': '/logos-natacao/corinthians.png',
    'SPORT CLUB CORINTHIANS PAULISTA': '/logos-natacao/corinthians.png',
    'FLAMENGO': '/logos-natacao/flamengo.png',
    'CLUBE DE REGATAS DO FLAMENGO': '/logos-natacao/flamengo.png',
    'CR FLAMENGO': '/logos-natacao/flamengo.png',
    'CR FLAMENGO/RJ': '/logos-natacao/flamengo.png',
    'FLUMINENSE': '/logo natacao/fluminense.png',
    'FLUMINENSE FOOTBALL CLUB': '/logo natacao/fluminense.png',
    'FLUMINENSE/RJ': '/logo natacao/fluminense.png',
    'FLUMINENSE FC': '/logo natacao/fluminense.png',
    'FLUMINENSE F.C.': '/logo natacao/fluminense.png',
    'FLUMINENSE FC/RJ': '/logo natacao/fluminense.png',
    'GRÊMIO NÁUTICO UNIÃO': '/logo natacao/gnu.svg',
    'GNU': '/logo natacao/gnu.svg',
    'BOTAFOGO': '/logos-natacao/botafogo.png',
    'CURITIBANO': '/logos-natacao/curitibano.png',
    'PAINEIRAS': '/logos-natacao/paineras.jpg',
    'CLUBE PAINEIRAS DO MORUMBY': '/logos-natacao/paineras.jpg',
    'SESI-SP': '/logos-natacao/sesi-sp.png',
    'AABB': '/logo natacao/aabb.png',
    'PRAIA CLUBE': '/logo natacao/praia-clube.png',
    'IDEAL CLUBE': '/logo natacao/ideal-clube.png',
    'SESC-RJ': '/logo natacao/sesc-rj.png',
    'SESC RJ': '/logo natacao/sesc-rj.png',
    'SESC': '/logo natacao/sesc.png',
    'TIJUCA': '/logo natacao/tijuca.png',
    'TIJUCA TC/RJ': '/logo natacao/tijuca.png',
    'AMERICANA': '/logo campeonatos natacao/americana.jpg',
    'CURITIBANO': '/logos-natacao/curitibano.png',
    'CLUBE CURITIBANO': '/logos-natacao/curitibano.png',
    'INDAIATUBA': '/logo campeonatos natacao/indaiatuba.jpeg',
    'NOVOS CIELOS': '/logo campeonatos natacao/novoscielos.png',
    'RECREIO DA JUVENTUDE': '/logos-natacao/recreiodajuventude.jpg',
    'SÃO CAETANO': '/logos-natacao/sao-caetano.jpeg',
    'SAO CAETANO': '/logos-natacao/sao-caetano.jpeg',
    'ASSOCIACAO ATLETICA SAO CAETANO': '/logos-natacao/sao-caetano.jpeg',
    'ASSOCIAÇÃO ATLÉTICA SÃO CAETANO': '/logos-natacao/sao-caetano.jpeg',
    'NATAÇÃO SÃO CAETANO': '/logos-natacao/sao-caetano.jpeg',
    'NATACAO SAO CAETANO': '/logos-natacao/sao-caetano.jpeg',
    'A.D. CENTRO OLIMPICO': '/logo natacao/ad-centro-olimpico.png',
    'AD CENTRO OLIMPICO': '/logo natacao/ad-centro-olimpico.png',
    'AEST/ARCELORMITTAL': '/logo natacao/aest.png',
    'AEST': '/logo natacao/aest.png',
    'COPM - PB': '/logo natacao/copm.png',
    'COPM': '/logo natacao/copm.png',
    'PEDRO NICOLAS': '/logo natacao/pedro-nicolas.png',
    'SWIM FLORIPA': '/logos-natacao/swimfloripa.avif',
    'SWIMFLORIPA': '/logos-natacao/swimfloripa.avif',
    'SWIMFLORIPA - JURERÊ': '/logos-natacao/swimfloripa.avif',
    'SWIMFLORIPA - JURERE': '/logos-natacao/swimfloripa.avif',
    'JUSC/SWIMFLORIPA': '/logos-natacao/swimfloripa.avif',
    // Australian clubs
    'ALL SAINTS': '/logos clubes australia/all-saints.jpeg',
    'BENDIGO EAST': '/logos clubes australia/bendigo-east.jpeg',
    'BOND': '/logos clubes australia/bond.jpg',
    'BRISBANE GRAMMAR': '/logos clubes australia/brisbane-grammar.jpg',
    'CARLILE': '/logos clubes australia/carille.png',
    'CAULFIELD': '/logos clubes australia/caufield.png',
    'CHANDLER': '/logos clubes australia/chandler.jpeg',
    'CRUIZ': '/logos clubes australia/cruiz.png',
    'GRIFFITH UNIVERSITY': '/logos clubes australia/griffith-university.png',
    'HIGHLANDERS': '/logos clubes australia/highlanders.jpeg',
    'IONA': '/logos clubes australia/iona.png',
    'MARION': '/logos clubes australia/marion.jpeg',
    'MELBOURNE VICENTRE': '/logos clubes australia/melbourne-vicentre.jpeg',
    'MELBOURNE': '/logos clubes australia/melbourne.svg',
    'MIAMI': '/logos clubes australia/miami.png',
    'NUDGEE COLLEGE': '/logos clubes australia/nudgee.png',
    'NUNAWADING': '/logos clubes australia/nunawading.png',
    'NUSWIM': '/logos clubes australia/nuswim.png',
    'PROPULSION': '/logos clubes australia/propulsion.png',
    'MELBOURNE PROPULSION': '/logos clubes australia/propulsion.png',
    'RACKLEY': '/logos clubes australia/rackley.png',
    'ROCKY CITY': '/logos clubes australia/rocky-city.jpeg',
    'SOPAC': '/logos clubes australia/sopac.jpeg',
    'SOUTHPORT OLYMPIC': '/logos clubes australia/southport-olympic.jpg',
    "ST ANDREW'S": '/logos clubes australia/st-andrews.png',
    'ST PETERS WESTERN': '/logos clubes australia/st-peters-western.png',
    'SUNSHINE COAST GRAMMAR': '/logos clubes australia/sunshine-coast-grammar.jpeg',
    'TOOWOOMBA GRAMMAR': '/logos clubes australia/toowoomba-gramar.jpg',
    'TRINITY GRAMMAR': '/logos clubes australia/trinity.png',
    'USC SPARTANS': '/logos clubes australia/usc-spartans.jpg',
    'UWA WEST COAST': '/logos clubes australia/uwa-west-coast.jpeg',
    'UWA-WEST COAST': '/logos clubes australia/uwa-west-coast.jpeg',
    'WARRINGAH': '/logos clubes australia/warrigah.png',
    'WARRINGAH AQUATIC': '/logos clubes australia/warrigah.png',
    'WESTS ILLAWARRA': '/logos clubes australia/west-illawarra.jpeg',
    'BRISBANE JETS': '/logos clubes australia/brisbane-jets.png',
    'CRANBROOK': '/logos clubes australia/cranbrook.png',
    'CRANBROOK SCHOOL': '/logos clubes australia/cranbrook.png',
    'IMMANUEL': '/logos clubes australia/immanuel.png',
    'IMMANUEL COLLEGE': '/logos clubes australia/immanuel.png',
    'KNOX PYMBLE': '/logos clubes australia/knox-pymble.jpeg',
    'KNOX-PYMBLE': '/logos clubes australia/knox-pymble.jpeg',
    'MANLY': '/logos clubes australia/manly.png',
    'MANLY SWIMMING': '/logos clubes australia/manly.png',
    'MCA': '/logos clubes australia/mca.png',
    'MLC AQUATIC': '/logos clubes australia/mlc-aquatic.png',
    'MLC': '/logos clubes australia/mlc-aquatic.png',
    'NORWOOD': '/logos clubes australia/norwood.jpeg',
    'NORWOOD SWIMMING': '/logos clubes australia/norwood.jpeg',
    'SYDNEY UNIVERSITY': '/logos clubes australia/sidney-university.jpg',
    'SYDNEY UNI': '/logos clubes australia/sidney-university.jpg',
    'SOMERVILLE HOUSE': '/logos clubes australia/sommerville-house.png',
    'SOMERVILLE': '/logos clubes australia/sommerville-house.png',
    'SURREY PARK': '/logos clubes australia/surrey-park.png',
    'SURREY PARK SWIMMING': '/logos clubes australia/surrey-park.png',
    'TSS AQUATIC': '/logos clubes australia/tss-aquatic.jpg',
    'TSS': '/logos clubes australia/tss-aquatic.jpg',
    'APAN/WS': '/logo natacao/apan-ws.jpeg',
    'APAN WS': '/logo natacao/apan-ws.jpeg',
    'BAURUENSE': '/logo natacao/bauruense.png',
    'BAURUENSE ABDA': '/logo natacao/bauruense.png',
    'CIRCULO MILITAR': '/logo natacao/circulo-militar.jpg',
    'CÍRCULO MILITAR': '/logo natacao/circulo-militar.jpg',
    'CNRAC': '/logo natacao/cnrac.png',
    'HEBRAICA': '/logo natacao/hebraica.jpg',
    'MBTC': '/logo natacao/mbtc.jpg',
    'NATAÇÃO OSASCO': '/logo natacao/natacao-osasco.png',
    'NATACAO OSASCO': '/logo natacao/natacao-osasco.png',
    'NATAÇÃO OSASCO / FAE': '/logo natacao/natacao-osasco.png',
    'PAULISTANO': '/logo natacao/paulistano.png',
    'PRAIA GRANDE': '/logo natacao/praia-grande.jpg',
    'WINNERS': '/logo natacao/winners.jpeg',
    'WINNERS/DKMBANK': '/logo natacao/winners.jpeg',
    'YATCH BA': '/logo natacao/yatch-ba.png',
    'YACHT CLUB/BA': '/logo natacao/yatch-ba.png',
    'AABB-RECIFE': '/logo natacao/aabb.png',
  };
  
  // Validar se clubName existe
  if (!clubName) {
    if (DEBUG.logos) console.warn('⚠️ clubName null/undefined');
    return '/logos-natacao/default-club.svg';
  }
  
  const normalizedName = clubName.toUpperCase().trim();
  let logo = clubLogos[normalizedName] || null;

  // Fallback por substring para clubes grandes com muitas variantes de nome
  if (!logo) {
    if (normalizedName.includes('CORINTHIANS')) {
      logo = '/logos-natacao/corinthians.png';
    } else if (normalizedName.includes('FLUMINENSE')) {
      logo = '/logo natacao/fluminense.png';
    } else if (normalizedName.includes('FLAMENGO')) {
      logo = '/logos-natacao/flamengo.png';
    }
  }

  // Fallback inteligente baseado no nome → slug para arquivos de public
  if (!logo) {
    const slug = clubName
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');

    const candidates = [
      `/logos-natacao/${slug}.png`,
      `/logos-natacao/${slug}.svg`,
      `/logos-natacao/${slug}.jpg`,
      `/logos-natacao/${slug}.jpeg`,
      `/logo natacao/${slug}.png`,
      `/logo natacao/${slug}.svg`,
      `/logo natacao/${slug}.jpg`,
      `/logo natacao/${slug}.jpeg`,
    ];

    logo = candidates[0];

    if (DEBUG.logos) {
      console.log(' getClubLogo slug fallback:', {
        rawName: clubName,
        normalized: normalizedName,
        slug,
        candidates,
        chosen: logo,
      });
    }
  }

  if (!logo) logo = '/logos-natacao/default-club.svg';
  
  if (DEBUG.logos) {
    console.log(' Normalizado:', normalizedName, '→', logo);
  }
  
  return logo;
};

// === MAPEAMENTO DE LOGOS DOS CAMPEONATOS ===
const getCompetitionLogo = (competitionName) => {
  if (DEBUG.logos) console.log(' getCompetitionLogo:', competitionName);
  
  const competitionLogos = {
    // Brasileiras
    'TROFÉU JOSÉ FINKEL': '/logo campeonatos natacao/josefinkel.jpg',
    'TROFÉU JOSE FINKEL': '/logo campeonatos natacao/josefinkel.jpg',
    'TROFEU JOSE FINKEL': '/logo campeonatos natacao/josefinkel.jpg',
    'TROFÉU MARIA LENK': '/logo campeonatos natacao/marialenk.jpg',
    'TROFEU MARIA LENK': '/logo campeonatos natacao/marialenk.jpg',
    // Paris 2024
    'PARIS 2024': '/logo campeonatos natacao/paris2024.png',
    'OLIMPÍADAS PARIS 2024': '/logo campeonatos natacao/paris2024.png',
    'OLIMPIADAS PARIS 2024': '/logo campeonatos natacao/paris2024.png',
    'OLIMPÍADAS DE PARIS': '/logo campeonatos natacao/paris2024.png',
    'OLIMPIADAS DE PARIS': '/logo campeonatos natacao/paris2024.png',
    'OLIMPÍADAS DE PARIS 2024': '/logo campeonatos natacao/paris2024.png',
    'OLIMPIADAS DE PARIS 2024': '/logo campeonatos natacao/paris2024.png',
    'JOGOS OLÍMPICOS PARIS 2024': '/logo campeonatos natacao/paris2024.png',
    'JOGOS OLIMPICOS PARIS 2024': '/logo campeonatos natacao/paris2024.png',
    // Competições Australianas
    'JUNIOR PAN PACIFIC CHAMPIONSHIPS': '/logo campeonatos natacao/juniorpanpacs.png',
    'AUSTRALIAN SWIMMING TRIALS': '/logo campeonatos natacao/austrao trials.png',
    // Competições Americanas (USA Swimming)
    'TOYOTA NATIONAL CHAMPIONSHIPS': '/logo campeonatos natacao/toyota-us-open-championships-logo.png',
    'US TOYOTA NATIONAL CHAMPIONSHIPS': '/logo campeonatos natacao/toyota-us-open-championships-logo.png',
    'USA SWIMMING NATIONAL CHAMPIONSHIPS': '/logo campeonatos natacao/toyota-us-open-championships-logo.png',
    'US NATIONAL CHAMPIONSHIPS': '/logo campeonatos natacao/toyota-us-open-championships-logo.png',
    // Campeonatos Mundiais
    'WORLD AQUATICS CHAMPIONSHIPS': '/logo campeonatos natacao/wolidchampionshipsdoha2024.png',
    'WORLD CHAMPIONSHIPS': '/logo campeonatos natacao/wolidchampionshipsdoha2024.png',
    'DOHA 2024': '/logo campeonatos natacao/wolidchampionshipsdoha2024.png',
    'SINGAPORE 2025': '/logo campeonatos natacao/wolidchampionshipssingapure2025.png',
    // US Open
    'US OPEN': '/logo campeonatos natacao/toyota-us-open-championships-logo.png',
    'TOYOTA US OPEN': '/logo campeonatos natacao/toyota-us-open-championships-logo.png',
    'US OPEN CHAMPIONSHIPS': '/logo campeonatos natacao/toyota-us-open-championships-logo.png',
    // CBDA
    'CBDA': '/logo campeonatos natacao/cbda.png',
    'CAMPEONATO BRASILEIRO': '/logo campeonatos natacao/cbda.png',
    'BRASILEIRO DE NATAÇÃO': '/logo campeonatos natacao/cbda.png',
    // Campeonatos/meetings por clube/cidade
    'AMERICANA': '/logo campeonatos natacao/americana.jpg',
    'CURITIBANO': '/logo campeonatos natacao/curitibano.png',
    'CLUBE CURITIBANO': '/logo campeonatos natacao/curitibano.png',
    'INDAIATUBA': '/logo campeonatos natacao/indaiatuba.jpeg',
    'NOVOS CIELOS': '/logo campeonatos natacao/novoscielos.png',
    'PAINEIRAS': '/logo campeonatos natacao/paineras.jpg',
    'CLUBE PAINEIRAS': '/logo campeonatos natacao/paineras.jpg',
    'RECREIO DA JUVENTUDE': '/logo campeonatos natacao/recreiodajuventude.jpg',
    'SÃO CAETANO': '/logo campeonatos natacao/sao caetano.jpeg',
    'SAO CAETANO': '/logo campeonatos natacao/sao caetano.jpeg',
    'SWIM FLORIPA': '/logo campeonatos natacao/swimfloripa.avif',
    'SWIMFLORIPA': '/logo campeonatos natacao/swimfloripa.avif',
    'TIJUCA': '/logo campeonatos natacao/tijuca.png',
    'TIJUCA TÊNIS CLUBE': '/logo campeonatos natacao/tijuca.png',
  };
  
  // Validar se competitionName existe
  if (!competitionName) {
    console.warn('⚠️ getCompetitionLogo: competitionName está null/undefined');
    return null;
  }
  
  const normalizedName = competitionName.toUpperCase().trim();
  const logo = competitionLogos[normalizedName];
  
  if (DEBUG.logos) {
    console.log(' Normalizado:', normalizedName, '→', logo);
  }
  
  return logo;
};

// === ÍCONES PROFISSIONAIS SEM EMOJIS ===
const BrandIcon = ({ type, className = "" }) => {
  const icons = {
    trophy: (
      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${className}`} 
           style={{ backgroundColor: BRAND_GOLD, color: 'white' }}
           aria-hidden="true">
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 01-2 0V6H3a1 1 0 010-2h1V3a1 1 0 011-1zm0 10a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1v-1a1 1 0 011-1zM12 2a1 1 0 01.967.744L14.146 7.2 17.5 9.134a1 1 0 010 1.732L14.146 12.8l-1.179 4.456a1 1 0 01-1.934 0L9.854 12.8 6.5 10.866a1 1 0 010-1.732L9.854 7.2l1.179-4.456A1 1 0 0112 2z" clipRule="evenodd" />
        </svg>
      </div>
    ),
    swim: (
      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${className}`} 
           style={{ backgroundColor: BRAND_GREEN, color: 'white' }}>
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path d="M2 10c0-1.5.5-2.5 1.5-3.5S5.5 5 7 5c1 0 2 .5 2.5 1.5S10.5 8.5 11 9c.5.5 1.5 1 2.5 1s2-.5 2.5-1.5S17.5 6.5 19 6v2c-1 0-2 .5-2.5 1.5S15.5 11.5 15 12c-.5.5-1.5 1-2.5 1s-2-.5-2.5-1.5S8.5 9.5 8 9c-.5-.5-1.5-1-2.5-1S3.5 8.5 3 9.5 2.5 11.5 2 12v-2z"/>
        </svg>
      </div>
    ),
    stats: (
      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${className}`} 
           style={{ backgroundColor: BRAND_GOLD, color: 'white' }}>
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
        </svg>
      </div>
    ),
    calendar: (
      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${className}`} 
           style={{ backgroundColor: BRAND_GREEN, color: 'white' }}>
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
        </svg>
      </div>
    ),
    fire: (
      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${className}`} 
           style={{ backgroundColor: BRAND_GOLD, color: 'white' }}>
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" />
        </svg>
      </div>
    ),
    medal: (
      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${className}`} 
           style={{ backgroundColor: BRAND_GOLD, color: 'white' }}>
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10 2L7 8H2l5 4-2 6 5-3 5 3-2-6 5-4h-5z" />
        </svg>
      </div>
    ),
    pool: (
      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${className}`} 
           style={{ backgroundColor: BRAND_GREEN, color: 'white' }}>
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path d="M4 3a2 2 0 100 4h12a2 2 0 100-4H4z" />
          <path fillRule="evenodd" d="M3 8a1 1 0 011-1h12a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1V8zm2 2a1 1 0 011-1h6a1 1 0 110 2H6a1 1 0 01-1-1z" clipRule="evenodd" />
        </svg>
      </div>
    ),
    target: (
      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${className}`} 
           style={{ backgroundColor: BRAND_GREEN, color: 'white' }}>
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM6.75 9.25a3.25 3.25 0 116.5 0 3.25 3.25 0 01-6.5 0zM10 8a1 1 0 100 2 1 1 0 000-2z" clipRule="evenodd" />
        </svg>
      </div>
    )
  };
  return icons[type] || null;
};

// === NOMES DE EXIBIÇÃO E VENUES DAS COMPETIÇÕES ===
const getCompetitionDisplayName = (fullName) => {
  if (!fullName) return fullName;
  
  const displayNames = {
    // Internacionais
    'OLIMPIADAS DE PARIS': 'Olimpíadas',
    'WORLD AQUATICS CHAMPIONSHIPS': 'Mundial',
    'WORLD AQUATICS JUNIOR SWIMMING CHAMPIONSHIPS': 'Mundial Junior',
    'EUROPEAN AQUATICS CHAMPIONSHIPS': 'Europeu',
    
    // Trials
    'AUSTRALIAN SWIMMING TRIALS': 'Australian Trials',
    'US OLYMPIC TEAM TRIALS SWIMMING': 'Olympic Trials',
    'PHILLIPS 66 NATIONAL CHAMPIONSHIPS': 'Phillips 66 Nationals',
    
    // Naming Rights (manter nome completo)
    'TYR PRO SWIM SERIES': 'TYR Pro Swim Series',
    'TOYOTA NATIONAL CHAMPIONSHIPS': 'Toyota Nationals',
    
    // Brasileiros
    'TROFEU MARIA LENK': 'Maria Lenk',
    'TROFEU JOSE FINKEL': 'José Finkel',
    'CAMPEONATO BRASILEIRO DE SELEÇÕES': 'Brasileiro de Seleções',
  };
  
  const normalized = fullName.toUpperCase().trim();
  
  // Busca exata
  if (displayNames[normalized]) {
    return displayNames[normalized];
  }
  
  // Busca parcial (contém)
  for (const [key, value] of Object.entries(displayNames)) {
    if (normalized.includes(key)) {
      return value;
    }
  }
  
  // Se não encontrar, retorna o nome sem o ano
  return fullName.replace(/\s*\d{4}\s*$/g, '').trim();
};

const getCompetitionVenue = (fullName, country) => {
  if (!fullName) return '';
  
  const venues = {
    // Com local conhecido
    'OLIMPIADAS DE PARIS': 'Paris',
    'WORLD AQUATICS CHAMPIONSHIPS': 'Singapura', // 2025
    'WORLD AQUATICS JUNIOR SWIMMING CHAMPIONSHIPS': 'Otopeni', // 2025
    'EUROPEAN AQUATICS CHAMPIONSHIPS': 'Belgrado', // 2024
    'AUSTRALIAN SWIMMING TRIALS': 'Brisbane',
    'US OLYMPIC TEAM TRIALS SWIMMING': 'Indianapolis',
    'PHILLIPS 66 NATIONAL CHAMPIONSHIPS': 'Indianapolis',
    'TYR PRO SWIM SERIES': 'Westmont', // 2025
    'TOYOTA NATIONAL CHAMPIONSHIPS': 'Indianapolis',
    
    // Brasileiros - sem venue, mostrar ano
    'TROFEU MARIA LENK': '',
    'TROFEU JOSE FINKEL': '',
    'CAMPEONATO BRASILEIRO DE SELEÇÕES': '',
  };
  
  const normalized = fullName.toUpperCase().trim();
  
  // Busca exata
  if (venues.hasOwnProperty(normalized)) {
    return venues[normalized];
  }
  
  // Busca parcial
  for (const [key, value] of Object.entries(venues)) {
    if (normalized.includes(key)) {
      return value;
    }
  }
  
  // Se não encontrar e tiver país, retorna país
  return country || '';
};

// === CONFIGURAÇÃO DE COMPETIÇÕES DE NATAÇÃO ===
const DEFAULT_YEAR = 2024;

// Competições padrão de fallback
const DEFAULT_COMPETITIONS_FALLBACK = [
  { id: 1, name: 'Olimpíadas Paris 2024', year: 2024, tipo_torneio: 'Internacional', country: 'França' },
  { id: 2, name: 'Troféu Maria Lenk 2024', year: 2024, tipo_torneio: 'Nacional', country: 'Brasil' },
  { id: 3, name: 'Troféu Maria Lenk 2023', year: 2023, tipo_torneio: 'Nacional', country: 'Brasil' },
];

// Competição padrão
const DEFAULT_COMPETITION = { 
  id: 1, 
  name: 'Olimpíadas Paris 2024', 
  year: 2024,
  tipo_torneio: 'Internacional',
  country: 'França'
};

const SWIM_LABELS = {
  pt: {
    pageTitle: 'Natação — Stats Hub',
    pageSubtitle: 'Resultados, campeões, médias e comparativos (dados reais do banco)',
    badgeText: 'NATAÇÃO',
    yearLabel: 'Ano',
    loadingComps: 'Carregando competições...',
    competitionLabel: 'Competição',
    allCompetitions: 'Todas as Competições',
    genderLabel: 'Gênero',
    typeLabel: 'Tipo',
    eventLabel: 'Prova',
    phaseLabel: 'Fase',
    feminine: 'Feminino',
    masculine: 'Masculino',
    individual: 'Individual',
    relay: 'Revezamento',
    noEventAvail: 'Nenhuma prova disponível',
    noPhaseAvail: 'Nenhuma fase disponível',
    loadingResults: 'Carregando resultados...',
    errLoadingResults: 'Erro ao carregar resultados',
    noResultsFound: 'Nenhum resultado encontrado',
    noResultsHint: 'Tente selecionar outra competição, prova ou fase',
    colPos: 'Pos.',
    colAthlete: 'Atleta',
    colClub: 'Clube/País',
    colTime: 'Tempo',
    colDiff: 'Dif.',
    clickAthlete: 'Clique no atleta para ver perfil completo',
    firstPlace: '1º Lugar (Destaque)',
    sectionResults: 'Resultados da Prova',
    sectionChampions: 'Campeões por Competição - Tempos Vencedores',
    sectionAvgTime: 'Análise do Tempo Médio Finais - Todas as Competições',
    sectionDiff: 'Diferença 1º vs 8º nas Finais - Todas as Competições',
    compsAvailable: (n) => `${n} Competições Disponíveis`,
    dataUpdated: 'Dados Atualizados',
    detailedAnalysis: 'Análises Detalhadas',
    noChampions: (y) => `Sem dados de campeões para ${y}`,
    noEvents: (g) => `Nenhum evento disponível para ${g === 'F' ? 'Feminino' : 'Masculino'}`,
    noData: 'Sem dados',
    noFinalData: 'Sem dados de finais',
    hideSplits: 'Esconder splits',
    showSplits: 'Ver splits',
    timeLabel: 'Tempo',
    splitsLabel: 'Splits:',
    femSection: 'Feminino',
    mascSection: 'Masculino',
  },
  en: {
    pageTitle: 'Swimming — Stats Hub',
    pageSubtitle: 'Results, champions, averages and comparisons (real database data)',
    badgeText: 'SWIMMING',
    yearLabel: 'Year',
    loadingComps: 'Loading competitions...',
    competitionLabel: 'Competition',
    allCompetitions: 'All Competitions',
    genderLabel: 'Gender',
    typeLabel: 'Type',
    eventLabel: 'Event',
    phaseLabel: 'Phase',
    feminine: 'Female',
    masculine: 'Male',
    individual: 'Individual',
    relay: 'Relay',
    noEventAvail: 'No events available',
    noPhaseAvail: 'No phases available',
    loadingResults: 'Loading results...',
    errLoadingResults: 'Error loading results',
    noResultsFound: 'No results found',
    noResultsHint: 'Try selecting another competition, event or phase',
    colPos: 'Pos.',
    colAthlete: 'Athlete',
    colClub: 'Club/Country',
    colTime: 'Time',
    colDiff: 'Diff.',
    clickAthlete: 'Click on athlete to view full profile',
    firstPlace: '1st Place (Highlight)',
    sectionResults: 'Event Results',
    sectionChampions: 'Champions by Competition - Winning Times',
    sectionAvgTime: 'Finals Average Time Analysis - All Competitions',
    sectionDiff: '1st vs 8th Difference in Finals - All Competitions',
    compsAvailable: (n) => `${n} Competitions Available`,
    dataUpdated: 'Data Updated',
    detailedAnalysis: 'Detailed Analyses',
    noChampions: (y) => `No champion data for ${y}`,
    noEvents: (g) => `No events available for ${g === 'F' ? 'Female' : 'Male'}`,
    noData: 'No data',
    noFinalData: 'No finals data',
    hideSplits: 'Hide splits',
    showSplits: 'View splits',
    timeLabel: 'Time',
    splitsLabel: 'Splits:',
    femSection: 'Female',
    mascSection: 'Male',
  }
};

/** Seletor profissional de competições com opção "Todas" */
const CompetitionSelector = ({ selectedCompetition, onCompetitionChange, availableCompetitions, showAllOption = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedLogo = getCompetitionLogo(selectedCompetition.name);
  const displayName = getCompetitionDisplayName(selectedCompetition.name);
  const venue = getCompetitionVenue(selectedCompetition.name, selectedCompetition.country);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full h-11 px-4 bg-white border border-gray-200 rounded-xl text-left font-semibold transition-all duration-200 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-green)] focus:border-transparent"
        style={{ color: 'var(--brand-green)' }}
        aria-expanded={isOpen}
      >
        <div className="flex items-center gap-3">
          {selectedLogo ? (
            <img 
              src={selectedLogo} 
              alt={displayName}
              className="w-6 h-6 object-contain rounded"
              onError={(e) => {
                console.error(' Erro ao carregar logo:', selectedLogo);
                e.target.style.display = 'none';
              }}
            />
          ) : (
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: BRAND_GREEN }} />
          )}
          <span>{displayName}</span>
          {venue && (
            <span className="text-sm text-gray-500 font-normal">{venue}</span>
          )}
        </div>
        <svg className={`w-5 h-5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
             fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-sm max-h-60 overflow-y-auto"
             style={{ zIndex: 9999 }}>
          {showAllOption && (
            <button
              type="button"
              onClick={() => {
                onCompetitionChange({ id: null, name: 'Todas as Competições', year: 'Todos', country: 'Global' });
                setIsOpen(false);
              }}
              className={`w-full px-4 py-3 text-left transition-colors duration-150 flex items-center justify-between ${
                selectedCompetition.id === null 
                  ? 'font-semibold bg-[var(--brand-green-100)] text-[var(--brand-green)]' 
                  : 'font-medium text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full" 
                     style={{ backgroundColor: selectedCompetition.id === null ? BRAND_GREEN : '#d1d5db' }} />
                <span>Todas as Competições</span>
                <span className="text-sm text-gray-500">(Global)</span>
              </div>
              {selectedCompetition.id === null && (
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: BRAND_GOLD }} />
              )}
            </button>
          )}
          {availableCompetitions.map((competition) => {
            const competitionLogo = getCompetitionLogo(competition.name);
            const compDisplayName = getCompetitionDisplayName(competition.name);
            const compVenue = getCompetitionVenue(competition.name, competition.country);
            
            return (
              <button
                key={competition.id}
                type="button"
                onClick={() => {
                  onCompetitionChange(competition);
                  setIsOpen(false);
                }}
                className={`w-full px-4 py-3 text-left transition-colors duration-150 flex items-center justify-between ${
                  selectedCompetition.id === competition.id 
                    ? 'font-semibold bg-[var(--brand-green-100)] text-[var(--brand-green)]' 
                    : 'font-medium text-gray-700 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  {competitionLogo ? (
                    <img 
                      src={competitionLogo} 
                      alt={compDisplayName}
                      className="w-5 h-5 object-contain rounded"
                      onError={(e) => {
                        console.error(' Erro ao carregar logo:', competitionLogo);
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="w-2 h-2 rounded-full" 
                         style={{ backgroundColor: selectedCompetition.id === competition.id ? BRAND_GREEN : '#d1d5db' }} />
                  )}
                  <span>{compDisplayName}</span>
                  {compVenue && (
                    <span className="text-sm text-gray-500">{compVenue}</span>
                  )}
                </div>
                {selectedCompetition.id === competition.id && (
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: BRAND_GOLD }} />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// === COMPONENTE DE CARD DE RESULTADO DO ATLETA ===
const AthleteResultCard = ({ position, medal, athleteName, clubName, clubLogo, time, splits, bgColor, borderColor }) => {
  const { lang } = useSportsLang();
  const t = SWIM_LABELS[lang] || SWIM_LABELS.pt;
  const [showSplits, setShowSplits] = React.useState(false);

  return (
    <div className={`relative bg-gradient-to-br ${bgColor} rounded-xl border-2 ${borderColor} p-4 min-w-[240px] hover:shadow-lg transition-all duration-200 cursor-pointer`}>
      {/* Posição e Medalha */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-white"
               style={{ backgroundColor: position <= 3 ? BRAND_GOLD : BRAND_GREEN }}>
            {position}
          </div>
          {medal && <span className="text-2xl">{medal}</span>}
        </div>
        {splits && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowSplits(!showSplits);
            }}
            className="w-6 h-6 rounded-full bg-white border-2 border-gray-300 flex items-center justify-center hover:bg-gray-50 transition-colors"
            title={showSplits ? t.hideSplits : t.showSplits}
          >
            <span className="text-xs font-bold" style={{ color: BRAND_GREEN }}>
              {showSplits ? '−' : '+'}
            </span>
          </button>
        )}
      </div>

      {/* Logo do Clube */}
      <div className="flex justify-center mb-3">
        <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-white shadow-md bg-white">
          <img 
            src={clubLogo} 
            alt={clubName}
            className="w-full h-full object-contain p-1"
          />
        </div>
      </div>

      {/* Nome do Atleta */}
      <div className="text-center mb-2">
        <h3 className="font-bold text-sm line-clamp-1" style={{ color: BRAND_GREEN }}>
          {athleteName}
        </h3>
        <p className="text-xs text-gray-600 line-clamp-1 mt-1">
          {clubName}
        </p>
      </div>

      {/* Tempo */}
      <div className="text-center py-2 px-3 rounded-lg bg-white border border-gray-200">
        <div className="text-xs text-gray-500 mb-1">{t.timeLabel}</div>
        <div className="text-2xl font-bold" style={{ color: BRAND_GREEN }}>
          {time}
        </div>
      </div>

      {/* Splits (expandível) */}
      {showSplits && splits && (
        <div className="mt-3 pt-3 border-t border-gray-300">
          <div className="text-xs font-semibold text-gray-600 mb-2">{t.splitsLabel}</div>
          <div className="grid grid-cols-2 gap-2">
            {splits.map((split, idx) => (
              <div key={idx} className="bg-white rounded-lg px-2 py-1 text-center border border-gray-200">
                <div className="text-[10px] text-gray-500">50m #{idx + 1}</div>
                <div className="text-sm font-bold" style={{ color: BRAND_GREEN }}>{split}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default function SwimmingPage() {
  /** Hook de navegação */
  const navigate = useNavigate();
  const { lang } = useSportsLang();
  const t = SWIM_LABELS[lang] || SWIM_LABELS.pt;
  
  /** Estados */
  const [year, setYear] = useState(DEFAULT_YEAR);
  const [selectedCompetition, setSelectedCompetition] = useState(DEFAULT_COMPETITION);
  const [selectedCompetitionId, setSelectedCompetitionId] = useState(null);
  const [availableCompetitions, setAvailableCompetitions] = useState(DEFAULT_COMPETITIONS_FALLBACK);
  const [loadingCompetitions, setLoadingCompetitions] = useState(false);
  
  // Estados para dados reais da API
  const [competitions, setCompetitions] = useState([]);
  const [events, setEvents] = useState([]); // Eventos da competição selecionada
  const [allEvents, setAllEvents] = useState([]); // TODOS os eventos (para gráficos)
  const [results, setResults] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedRound, setSelectedRound] = useState('Final');
  const [selectedGender, setSelectedGender] = useState('F'); // Padrão: Feminino
  const [selectedRelayType, setSelectedRelayType] = useState('individual'); // 'individual' ou 'relay' (sem "Todos")
  const [selectedYear, setSelectedYear] = useState(2025); // Padrão: 2025
  const [availableYears, setAvailableYears] = useState([2026, 2025, 2024, 2023]); // Anos com dados
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Estados para filtros dinâmicos baseados na competição selecionada
  const [eventsWithPhases, setEventsWithPhases] = useState([]); // Eventos com suas fases disponíveis
  const [selectedEventPhases, setSelectedEventPhases] = useState([]); // Fases disponíveis na prova selecionada

  /** Auditoria: listar clubes brasileiros sem logo mapeada (log no console) */
  useEffect(() => {
    if (!DEBUG.logos) return;

    const auditBrazilianClubLogos = async () => {
      try {
        const clubs = await swimmingService.getClubs({ country: 'BRA' });
        if (!clubs || !Array.isArray(clubs)) return;

        const missing = [];
        clubs.forEach((club) => {
          const name = club.name || club.club_name || '';
          const logo = getClubLogo(name);
          if (logo.includes('default-club')) {
            missing.push(name);
          }
        });

        const uniqueMissing = Array.from(new Set(missing)).sort();
        console.log('🇧🇷 [Swimming] Clubes brasileiros sem logo mapeada:', uniqueMissing);
      } catch (err) {
        console.error(' Erro ao auditar logos de clubes brasileiros:', err);
      }
    };

    auditBrazilianClubLogos();
  }, []);

  /** Carregar competições do banco de dados */
  useEffect(() => {
    const loadCompetitions = async () => {
      try {
        setLoadingCompetitions(true);
        // Buscar TODAS as competições (sem filtro de ano)
        const response = await swimmingService.getCompetitions();
        
        // A API pode retornar { competitions: [...] } ou diretamente um array
        const data = Array.isArray(response) ? response : (response.competitions || []);
        setCompetitions(data);
        
        // Extrai anos únicos das competições disponíveis (em ordem decrescente)
        const yearsWithData = [...new Set(data.map(c => c.year))].sort((a, b) => b - a);
        if (yearsWithData.length > 0) {
          setAvailableYears(yearsWithData);
          
          // Se o ano selecionado não tem dados, muda para o ano mais recente
          if (!yearsWithData.includes(selectedYear)) {
            setSelectedYear(yearsWithData[0]);
          }
        }
        
        // Formata para o dropdown
        const formattedCompetitions = data.map(comp => ({
          id: comp.id,
          name: comp.name,
          year: comp.year
        }));
        
        // Filtra pelo ano selecionado (ou mais recente)
        const yearToUse = yearsWithData.includes(selectedYear) ? selectedYear : yearsWithData[0];
        const filtered = formattedCompetitions.filter(c => c.year === yearToUse);
        setAvailableCompetitions(filtered.length > 0 ? filtered : DEFAULT_COMPETITIONS_FALLBACK);
        
        // Seleciona competição padrão
        if (filtered.length > 0) {
          // Tenta Maria Lenk, senão primeira do ano
          const mariaLenk = filtered.find(c => c.name.toLowerCase().includes('maria lenk'));
          const mundial = filtered.find(c => c.name.toLowerCase().includes('world') || c.name.toLowerCase().includes('mundial'));
          const defaultComp = mariaLenk || mundial || filtered[0];
          setSelectedCompetition(defaultComp);
          setSelectedCompetitionId(defaultComp.id);
        }
      } catch (error) {
        console.error('Erro ao carregar competições:', error);
        setAvailableCompetitions(DEFAULT_COMPETITIONS_FALLBACK);
        setError('Erro ao carregar competições');
      } finally {
        setLoadingCompetitions(false);
      }
    };

    loadCompetitions();
  }, []); // Carrega apenas uma vez

  /** Carregar TODOS os eventos (para gráficos de análise) */
  useEffect(() => {
    const loadAllEvents = async () => {
      try {
        // Buscar todos os eventos sem filtro de competição
        const response = await swimmingService.getEvents({});
        const data = Array.isArray(response) ? response : (response.events || []);
        setAllEvents(data);
      } catch (error) {
        console.error(' Erro ao carregar todos os eventos:', error);
        setAllEvents([]);
      }
    };

    loadAllEvents();
  }, []); // Carrega apenas uma vez

  /** Filtrar competições pelo ano selecionado */
  useEffect(() => {
    if (competitions.length > 0) {
      // Filtra competições pelo ano
      const filtered = competitions.filter(c => c.year === selectedYear);
      
      const formattedFiltered = filtered.map(comp => ({
        id: comp.id,
        name: comp.name,
        year: comp.year
      }));
      
      setAvailableCompetitions(formattedFiltered.length > 0 ? formattedFiltered : []);
      
      // Seleciona primeira competição do ano como padrão
      if (formattedFiltered.length > 0) {
        // Prioriza Maria Lenk > Mundial > Primeira
        const mariaLenk = formattedFiltered.find(c => c.name.toLowerCase().includes('maria lenk'));
        const mundial = formattedFiltered.find(c => c.name.toLowerCase().includes('world') || c.name.toLowerCase().includes('mundial'));
        const defaultComp = mariaLenk || mundial || formattedFiltered[0];
        setSelectedCompetition(defaultComp);
        setSelectedCompetitionId(defaultComp.id);
      } else {
        // Se não há competições neste ano, limpa seleção
        setSelectedCompetition({ id: null, name: 'Nenhuma competição disponível', year: selectedYear });
        setSelectedCompetitionId(null);
      }
    }
  }, [selectedYear, competitions]);

  /** Carregar eventos com suas fases quando competição mudar */
  useEffect(() => {
    const loadCompetitionData = async () => {
      if (!selectedCompetitionId) {
        setEventsWithPhases([]);
        return;
      }

      try {
        if (DEBUG.events) {
          console.log(' Carregando eventos com fases para competição:', selectedCompetitionId);
        }
        
        // SOLUÇÃO TEMPORÁRIA: Busca todos os resultados da competição para extrair fases por evento
        try {
          const resultsResponse = await swimmingService.getResults({ 
            competition_id: selectedCompetitionId,
            limit: 10000  // Limite alto para pegar todos os resultados e fases
          });
          
          const results = Array.isArray(resultsResponse) ? resultsResponse : (resultsResponse.results || []);
          
          // Agrupa por evento e extrai fases únicas
          const eventPhasesMap = {};
          results.forEach(result => {
            const eventId = result.event_id;
            const phase = result.fase || result.phase;
            
            if (eventId && phase) {
              if (!eventPhasesMap[eventId]) {
                eventPhasesMap[eventId] = {
                  event_id: eventId,
                  event_name: result.event_name,
                  phases: new Set()
                };
              }
              eventPhasesMap[eventId].phases.add(phase);
            }
          });
          
          // DEBUG: Verificar fases únicas encontradas
          const allPhases = new Set();
          Object.values(eventPhasesMap).forEach(event => {
            event.phases.forEach(p => allPhases.add(p));
          });
          if (selectedCompetitionId === 42) {
            console.log(' TOYOTA DEBUG - Fases únicas encontradas:', Array.from(allPhases));
            console.log('� Exemplo de evento com fases:', Object.values(eventPhasesMap)[0]);
          }
          
          // Converte Sets em Arrays ordenados (usando nomes padronizados do banco)
          const phaseOrder = { 
            'Final': 1,
            'Final A': 1,
            'Final B': 2, 
            'Final C': 3, 
            'Final D': 4, 
            'Semifinal': 5,
            'Semifinals': 5,
            'Eliminatórias': 6,
            'Eliminatória': 6,
            'Eliminatorias': 6,
            'Heat': 6,
            'Heats': 6
          };
          const eventsData = Object.values(eventPhasesMap).map(event => ({
            ...event,
            phases: Array.from(event.phases).sort((a, b) => 
              (phaseOrder[a] || 999) - (phaseOrder[b] || 999)
            )
          }));
          
          setEventsWithPhases(eventsData);
          
        } catch (fallbackError) {
          console.error(' Erro ao carregar resultados:', fallbackError);
          // Se falhar, deixa vazio
          setEventsWithPhases([]);
        }
        
      } catch (error) {
        console.error(' Erro geral:', error);
      }
    };

    loadCompetitionData();
  }, [selectedCompetitionId]);

  /** Atualizar fases disponíveis quando a prova selecionada mudar */
  useEffect(() => {
    if (!selectedEvent || eventsWithPhases.length === 0) {
      setSelectedEventPhases([]);
      return;
    }

    // Encontra a prova selecionada em eventsWithPhases
    const currentEvent = eventsWithPhases.find(e => {
      const eventId = e.event_id || e.id;
      return eventId === selectedEvent;
    });

    if (currentEvent && currentEvent.phases && currentEvent.phases.length > 0) {
      setSelectedEventPhases(currentEvent.phases);
      
      // Se a fase atual não está disponível na nova prova, seleciona a primeira disponível
      if (!currentEvent.phases.includes(selectedRound)) {
        setSelectedRound(currentEvent.phases[0]);
      }
    } else {
      setSelectedEventPhases([]);
    }
  }, [selectedEvent, eventsWithPhases]);

  /** Carregar eventos (provas) filtrados por competição */
  useEffect(() => {
    const loadEvents = async () => {
      if (!selectedCompetitionId) {
        setEvents([]);
        setSelectedEvent(null); // Reset event when no competition
        return;
      }

      try {
        // Reset event quando trocar de competição para evitar usar event_id da competição anterior
        setSelectedEvent(null);
        
        const params = { competition_id: selectedCompetitionId };
        
        if (selectedGender) {
          params.gender = selectedGender;
        }
        if (selectedRelayType) {
          params.relay = selectedRelayType === 'relay' ? true : false;
        }
        
        const response = await swimmingService.getEvents(params);
        
        // A API pode retornar { events: [...] } ou diretamente um array
        const data = Array.isArray(response) ? response : (response.events || []);
        setEvents(data);
        
        // Seleciona o primeiro evento se houver
        if (data.length > 0) {
          const firstEventId = data[0].id || data[0].event_id;
          setSelectedEvent(firstEventId);
        } else {
          setSelectedEvent(null);
        }
      } catch (error) {
        console.error('Erro ao carregar eventos:', error);
        setError('Erro ao carregar eventos');
      }
    };

    loadEvents();
  }, [selectedCompetitionId, selectedGender, selectedRelayType]);

  /** Carregar resultados quando competição ou evento mudar */
  useEffect(() => {
    const loadResults = async () => {
      if (!selectedCompetitionId || !selectedEvent) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const params = {
          competition_id: selectedCompetitionId,
          event_id: selectedEvent,
          fase: selectedRound
        };
        
        if (DEBUG.results) {
          console.log(' Buscando:', params);
        }
        
        const response = await swimmingService.getResults(params);
        
        // A API pode retornar { results: [...] } ou diretamente um array
        const data = Array.isArray(response) ? response : (response.results || []);
        
        if (DEBUG.results) {
          console.log(' Retornou:', data.length, 'resultados');
          if (data.length > 0) {
            console.log('📋 Exemplo:', {
              nadador: data[0].swimmer_name,
              clube: data[0].club_name,
              tempo: data[0].time_formatted,
              fase: data[0].fase
            });
          }
        }
        
        setResults(data);
        setError(null);
      } catch (error) {
        console.error(' Erro ao carregar resultados:', error);
        setError(error.message || 'Erro ao carregar resultados');
        setResults([]);
      } finally {
        setLoading(false);
      }
    };

    loadResults();
  }, [selectedCompetitionId, selectedEvent, selectedRound]);

  // Loading state inicial
  if (loadingCompetitions && competitions.length === 0) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 mx-auto mb-4" 
               style={{ borderColor: BRAND_GREEN }}></div>
          <p className="text-lg font-semibold" style={{ color: BRAND_GREEN }}>{t.loadingComps}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 lg:px-6 space-y-6 w-full">
      {/* =========================================================
         HEADER PADRÃO ARENA STATS - NATAÇÃO
      ========================================================== */}
      <PageHeader
        title={t.pageTitle}
        subtitle={t.pageSubtitle}
        badgeText={t.badgeText}
        badgeIcon={true}
        rightSlot={
          <div className="min-w-[120px]">
            <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400 block mb-2">
              {t.yearLabel}
            </span>
            <select 
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base font-medium focus:ring-2 focus:ring-[var(--brand-green)] focus:border-transparent focus:outline-none"
              style={{ color: 'var(--brand-green)' }}
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
            >
              {availableYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        }
      />

      {/* =========================================================
         ESTATÍSTICAS E CONTEXTO (Hero antigo preservado)
      ========================================================== */}
      <div className="bg-white rounded-xl border border-gray-300 shadow-sm p-4 lg:p-5">
        <div className="flex items-center gap-4 text-sm flex-wrap">
          <div className="flex items-center gap-2">
            <BrandIcon type="pool" className="w-5 h-5" />
            <span className="font-semibold text-gray-700">{t.compsAvailable(competitions.length || 7)}</span>
          </div>
          <div className="flex items-center gap-2">
            <BrandIcon type="swim" className="w-5 h-5" />
            <span className="font-semibold text-gray-700">{t.dataUpdated}</span>
          </div>
          <div className="flex items-center gap-2">
            <BrandIcon type="stats" className="w-5 h-5" />
            <span className="font-semibold text-gray-700">{t.detailedAnalysis}</span>
          </div>
        </div>
      </div>

      {/* =========================================================
         RESULTADOS DE PROVA - SCROLL HORIZONTAL COM FILTROS
      ========================================================== */}
      <div className="bg-white rounded-xl border border-gray-300 shadow-sm p-4 lg:p-5 w-full min-w-0">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-1 h-8 rounded-full" style={{ backgroundColor: BRAND_GOLD }}></div>
            <BrandIcon type="trophy" className="w-6 h-6" />
            <h2 className="text-xl lg:text-2xl font-bold" style={{ color: 'var(--brand-green)' }}>
              {t.sectionResults}
            </h2>
          </div>
          
          {/* Seletor de Ano */}
          <div className="min-w-[120px]">
            <select 
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base font-medium focus:ring-2 focus:ring-[var(--brand-green)] focus:border-transparent focus:outline-none"
              style={{ color: 'var(--brand-green)' }}
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
            >
              {availableYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Filtros */}
        <div className="space-y-4 mb-4">
          {/* Scroll Horizontal de Competições */}
          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: BRAND_GREEN }}>{t.competitionLabel}</label>
            <div className="relative">
              <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                {availableCompetitions.map(comp => (
                  <button
                    key={comp.id}
                    onClick={() => {
                      if (DEBUG.clicks) console.log(' Competição:', comp.name, '| ID:', comp.id);
                      setSelectedCompetitionId(comp.id);
                      setSelectedCompetition(comp);
                    }}
                    className={`flex-shrink-0 px-5 py-4 rounded-lg border-2 transition-all duration-200 ${
                      selectedCompetitionId === comp.id
                        ? 'border-[var(--brand-green)] bg-gradient-to-br from-green-50 to-white shadow-md'
                        : 'border-gray-300 bg-white hover:border-gray-400 hover:shadow-sm'
                    }`}
                    style={{ minWidth: '220px' }}
                  >
                    <div className="flex items-center gap-3">
                      {getCompetitionLogo(comp.name) ? (
                        <img 
                          src={getCompetitionLogo(comp.name)} 
                          alt={comp.name}
                          className="w-10 h-10 object-contain rounded"
                          onError={(e) => {
                            console.error(' Erro ao carregar logo do botão:', getCompetitionLogo(comp.name));
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full flex items-center justify-center" 
                             style={{ backgroundColor: BRAND_GREEN }}>
                          <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 01-2 0V6H3a1 1 0 010-2h1V3a1 1 0 011-1zm0 10a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1v-1a1 1 0 011-1zM12 2a1 1 0 01.967.744L14.146 7.2 17.5 9.134a1 1 0 010 1.732L14.146 12.8l-1.179 4.456a1 1 0 01-1.934 0L9.854 12.8 6.5 10.866a1 1 0 010-1.732L9.854 7.2l1.179-4.456A1 1 0 0112 2z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                      <div className="text-left">
                        <div className="font-bold text-base" style={{ color: BRAND_GREEN }}>
                          {getCompetitionDisplayName(comp.name)}
                        </div>
                        <div className="text-sm text-gray-500 mt-1">
                          {getCompetitionVenue(comp.name, comp.country) || comp.year}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Filtros: Gênero, Tipo, Prova e Fase */}
          <div className="flex flex-wrap gap-3">
            {/* Seletor de Gênero */}
            <div className="flex-1 min-w-[160px]">
              <label className="block text-sm font-semibold text-gray-600 mb-1">{t.genderLabel}</label>
              <select 
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base font-medium focus:ring-2 focus:ring-[var(--brand-green)] focus:border-transparent focus:outline-none"
                style={{ color: 'var(--brand-green)' }}
                value={selectedGender}
                onChange={(e) => setSelectedGender(e.target.value)}
              >
                <option value="F">{t.feminine}</option>
                <option value="M">{t.masculine}</option>
              </select>
            </div>

            {/* Seletor de Tipo (Individual/Revezamento) */}
            <div className="flex-1 min-w-[160px]">
              <label className="block text-sm font-semibold text-gray-600 mb-1">{t.typeLabel}</label>
              <select 
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base font-medium focus:ring-2 focus:ring-[var(--brand-green)] focus:border-transparent focus:outline-none"
                style={{ color: 'var(--brand-green)' }}
                value={selectedRelayType}
                onChange={(e) => setSelectedRelayType(e.target.value)}
              >
                <option value="individual">{t.individual}</option>
                <option value="relay">{t.relay}</option>
              </select>
            </div>

            {/* Seletor de Prova */}
            <div className="flex-1 min-w-[240px]">
              <label className="block text-sm font-semibold text-gray-600 mb-1">{t.eventLabel}</label>
              <select 
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base font-medium focus:ring-2 focus:ring-[var(--brand-green)] focus:border-transparent focus:outline-none"
                style={{ color: 'var(--brand-green)' }}
                value={selectedEvent || ''}
                onChange={(e) => setSelectedEvent(Number(e.target.value))}
              >
                {/* Mostra todas as provas da competição */}
                {events.length === 0 ? (
                  <option>{t.noEventAvail}</option>
                ) : (
                  events.map(event => {
                    const eventId = event.event_id || event.id;
                    const eventName = event.event_name || formatEventName(
                      event.distance_meters || event.distance,
                      event.stroke,
                      event.gender,
                      event.relay || event.is_relay
                    );
                    return (
                      <option key={eventId} value={eventId}>
                        {eventName}
                      </option>
                    );
                  })
                )}
              </select>
            </div>

            {/* Seletor de Fase */}
            <div className="flex-1 min-w-[160px]">
              <label className="block text-sm font-semibold text-gray-600 mb-1">{t.phaseLabel}</label>
              <select 
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base font-medium focus:ring-2 focus:ring-[var(--brand-green)] focus:border-transparent focus:outline-none"
                style={{ color: 'var(--brand-green)' }}
                value={selectedRound}
                onChange={(e) => setSelectedRound(e.target.value)}
              >
                {selectedEventPhases.length === 0 ? (
                  <option>{t.noPhaseAvail}</option>
                ) : (
                  selectedEventPhases.map(phase => {
                    // Mapeia para português (usando nomes padronizados do banco)
                    const phaseNamePT = {
                      'Final': 'Final',
                      'Final A': 'Final A',
                      'Final B': 'Final B',
                      'Final C': 'Final C',
                      'Final D': 'Final D',
                      'Semifinal': 'Semifinal',
                      'Semifinals': 'Semifinal',
                      'Eliminatórias': 'Eliminatórias',
                      'Eliminatória': 'Eliminatórias',
                      'Eliminatorias': 'Eliminatórias',
                      'Heat': 'Eliminatórias',
                      'Heats': 'Eliminatórias'
                    }[phase] || phase;
                    
                    return (
                      <option key={phase} value={phase}>
                        {phaseNamePT}
                      </option>
                    );
                  })
                )}
              </select>
            </div>
          </div>
        </div>

        {/* Tabela de Resultados */}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-4 mx-auto mb-3" 
                     style={{ borderColor: BRAND_GREEN }}></div>
                <p className="text-sm font-semibold" style={{ color: BRAND_GREEN }}>{t.loadingResults}</p>
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-600 font-semibold mb-2">{t.errLoadingResults}</p>
              <p className="text-sm text-gray-600">{error}</p>
            </div>
          ) : results.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 font-semibold mb-2">{t.noResultsFound}</p>
              <p className="text-sm text-gray-500">{t.noResultsHint}</p>
            </div>
          ) : (
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b-2 border-gray-300">
                  <th className="text-left py-3 px-2 text-xs font-bold text-gray-700 uppercase tracking-wider w-16">{t.colPos}</th>
                  <th className="text-left py-3 px-3 text-xs font-bold text-gray-700 uppercase tracking-wider">{t.colAthlete}</th>
                  <th className="text-left py-3 px-3 text-xs font-bold text-gray-700 uppercase tracking-wider">{t.colClub}</th>
                  <th className="text-center py-3 px-3 text-xs font-bold text-gray-700 uppercase tracking-wider w-32">{t.colTime}</th>
                  <th className="text-center py-3 px-3 text-xs font-bold text-gray-700 uppercase tracking-wider w-24">{t.colDiff}</th>
                </tr>
              </thead>
              <tbody>
                {results.map((result, index) => {
                  const position = result.position || index + 1;
                  const fase = result.fase || 'Final';
                  
                  // Função para determinar estilo da posição baseado na fase
                  const getPositionStyle = (pos, fase) => {
                    const isFinal = fase === 'Final' || fase === 'Final A' || fase === 'Final B' || fase === 'Final C' || fase === 'Final D';
                    const isSemifinal = fase === 'Semifinal' || fase.includes('Semi');
                    const isEliminatoria = fase === 'Eliminatórias' || fase === 'Eliminatória' || fase === 'Eliminatorias' || fase.includes('Heat');
                    
                    if (isFinal) {
                      // FINAL
                      if (pos === 1) {
                        // 1º: Número dourado, bola dourada, dentro branco
                        return {
                          bg: '#B48C2C', // BRAND_GOLD
                          text: 'white',
                          border: '#B48C2C',
                          innerBg: 'white',
                          innerText: '#B48C2C',
                          rowBg: 'linear-gradient(to right, rgba(180, 140, 44, 0.08), transparent)'
                        };
                      } else if (pos === 2) {
                        // 2º: Prata
                        return {
                          bg: '#C0C0C0',
                          text: 'white',
                          border: '#C0C0C0',
                          innerBg: 'white',
                          innerText: '#C0C0C0',
                          rowBg: 'linear-gradient(to right, rgba(192, 192, 192, 0.08), transparent)'
                        };
                      } else if (pos === 3) {
                        // 3º: Bronze
                        return {
                          bg: '#CD7F32',
                          text: 'white',
                          border: '#CD7F32',
                          innerBg: 'white',
                          innerText: '#CD7F32',
                          rowBg: 'linear-gradient(to right, rgba(205, 127, 50, 0.08), transparent)'
                        };
                      } else if (pos >= 4 && pos <= 8) {
                        // 4º-8º: Verde com fundo verde claro
                        return {
                          bg: 'white',
                          text: BRAND_GREEN,
                          border: BRAND_GREEN,
                          innerBg: 'white',
                          innerText: BRAND_GREEN,
                          rowBg: 'transparent'
                        };
                      }
                    } else if (isSemifinal) {
                      // SEMIFINAL
                      if (pos <= 8) {
                        // 1º-8º: Verde com fundo branco
                        return {
                          bg: 'white',
                          text: BRAND_GREEN,
                          border: BRAND_GREEN,
                          innerBg: 'white',
                          innerText: BRAND_GREEN,
                          rowBg: 'transparent'
                        };
                      }
                    } else if (isEliminatoria) {
                      // ELIMINATÓRIAS
                      if (pos <= 16) {
                        // 1º-16º: Verde com fundo branco
                        return {
                          bg: 'white',
                          text: BRAND_GREEN,
                          border: BRAND_GREEN,
                          innerBg: 'white',
                          innerText: BRAND_GREEN,
                          rowBg: 'transparent'
                        };
                      }
                    }
                    
                    // Padrão: verde escuro
                    return {
                      bg: BRAND_GREEN,
                      text: 'white',
                      border: BRAND_GREEN,
                      innerBg: BRAND_GREEN,
                      innerText: 'white',
                      rowBg: 'transparent'
                    };
                  };
                  
                  const posStyle = getPositionStyle(position, fase);
                  
                  return (
                    <tr 
                      key={result.id || result.result_id || index}
                      className={`border-b border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer`}
                      style={{ background: posStyle.rowBg }}
                      onClick={() => navigate(`/nadador/${result.swimmer_id}`)}
                    >
                      {/* Posição */}
                      <td className="py-3 px-2">
                        <div 
                          className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm border-2"
                          style={{ 
                            backgroundColor: posStyle.innerBg,
                            color: posStyle.innerText,
                            borderColor: posStyle.border
                          }}
                        >
                          {position}º
                        </div>
                      </td>
                      
                      {/* Atleta */}
                      <td className="py-3 px-3">
                        <div className="font-semibold text-gray-900">{result.swimmer_display_name || result.swimmer_name}</div>
                      </td>
                      
                      {/* Clube/País com ícone */}
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-2">
                          {/* Se for seleção nacional (is_national_team=true), mostra bandeira */}
                          {result.is_national_team === true && result.nationality ? (
                            <img 
                              src={`/flags/${result.nationality}.svg`}
                              alt={result.nationality}
                              className="w-8 h-8 object-contain rounded"
                              onError={(e) => {
                                console.error('Erro ao carregar bandeira:', result.nationality);
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : (
                            /* Se não for seleção nacional, mostra logo do clube */
                            <img 
                              src={result.club_logo_url || getClubLogo(result.club_name)}
                              alt={result.club_name || 'Clube'}
                              className="w-8 h-8 rounded-full border border-gray-200 object-contain bg-white"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = '/logos-natacao/default-club.svg';
                              }}
                            />
                          )}
                          <span className="text-sm font-medium text-gray-700">{result.club_name || 'Independente'}</span>
                        </div>
                      </td>
                      
                      {/* Tempo */}
                      <td className="py-3 px-3 text-center">
                        <div className="font-bold text-lg" style={{ color: BRAND_GREEN }}>
                          {result.time_formatted || result.final_time || result.time_seconds}
                        </div>
                      </td>
                      
                      {/* Diferença */}
                      <td className="py-3 px-3 text-center">
                        {result.time_diff !== null && result.time_diff !== undefined && parseFloat(result.time_diff) > 0 ? (
                          <div className="text-sm font-medium text-gray-600">
                            +{parseFloat(result.time_diff).toFixed(2)}s
                          </div>
                        ) : (
                          <div className="text-sm font-bold text-gray-400">—</div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Legenda */}
        <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
          <div className="flex items-center gap-1">
            <BrandIcon type="fire" className="w-4 h-4" />
            <span>{t.clickAthlete}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full" style={{ backgroundColor: BRAND_GOLD }}></div>
            <span>{t.firstPlace}</span>
          </div>
        </div>
      </div>

      {/* === SEÇÃO: CAMPEÕES DE CADA COMPETIÇÃO === */}
      {allEvents.length > 0 && (
        <div className="mt-8 mb-12">
          <div className="mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-1 h-8 rounded-full" style={{ backgroundColor: BRAND_GOLD }}></div>
              <h2 className="text-2xl font-bold" style={{ color: BRAND_GREEN }}>
                {t.sectionChampions}
              </h2>
            </div>
            
            {/* Seletor de Ano */}
            <div className="flex items-center gap-3">
              <span className="text-sm font-semibold text-gray-700">Ano:</span>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="px-4 py-2 rounded-lg font-semibold border-2 transition-all duration-200 focus:outline-none focus:ring-2"
                style={{
                  borderColor: BRAND_GREEN,
                  color: BRAND_GREEN,
                  backgroundColor: 'white'
                }}
              >
                {[2025, 2024, 2023, 2022, 2021].map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Campeões Feminino */}
          <div className="mb-8">
            <h3 className="text-lg font-bold mb-4" style={{ color: BRAND_GREEN }}>{t.femSection}</h3>
            <ChampionsCardList 
              gender="F"
              allEvents={allEvents}
              allCompetitions={competitions}
              selectedYear={selectedYear}
              setSelectedYear={setSelectedYear}
            />
          </div>

          {/* Campeões Masculino */}
          <div>
            <h3 className="text-lg font-bold mb-4" style={{ color: BRAND_GREEN }}>{t.mascSection}</h3>
            <ChampionsCardList 
              gender="M"
              allEvents={allEvents}
              allCompetitions={competitions}
              selectedYear={selectedYear}
              setSelectedYear={setSelectedYear}
            />
          </div>
        </div>
      )}

      {/* === SEÇÃO: ANÁLISE DE TEMPO MÉDIO FINAIS === */}
      {allEvents.length > 0 && (
        <div className="mt-8 mb-12" style={{ overflow: 'visible' }}>
          <div className="mb-6 flex items-center gap-3">
            <div className="w-1 h-8 rounded-full" style={{ backgroundColor: BRAND_GOLD }}></div>
            <h2 className="text-2xl font-bold" style={{ color: BRAND_GREEN }}>
              {t.sectionAvgTime}
            </h2>
          </div>

          {/* Provas Femininas */}
          <div className="mb-8" style={{ overflow: 'visible' }}>
            <h3 className="text-lg font-bold mb-4" style={{ color: BRAND_GREEN }}>{t.femSection}</h3>
            <EventAverageCardList 
              key={`tempo-medio-f-${allEvents.length}`}
              gender="F"
              allEvents={allEvents}
              allCompetitions={competitions}
              selectedYear={selectedYear}
            />
          </div>

          {/* Provas Masculinas */}
          <div style={{ overflow: 'visible', minHeight: '500px' }}>
            <h3 className="text-lg font-bold mb-4" style={{ color: BRAND_GREEN }}>{t.mascSection}</h3>
            <EventAverageCardList 
              key={`tempo-medio-m-${allEvents.length}`}
              gender="M"
              allEvents={allEvents}
              allCompetitions={competitions}
              selectedYear={selectedYear}
            />
          </div>
        </div>
      )}

      {/* === SEÇÃO: DIFERENÇA 1º vs 8º NAS FINAIS (TODAS AS COMPETIÇÕES) === */}
      {allEvents.length > 0 && (
        <div className="mt-8 mb-12" style={{ overflow: 'visible' }}>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-8 rounded-full" style={{ backgroundColor: BRAND_GOLD }}></div>
            <h2 className="text-2xl font-bold" style={{ color: BRAND_GREEN }}>
              {t.sectionDiff}
            </h2>
          </div>

          {/* Provas Femininas */}
          <div className="mb-8" style={{ overflow: 'visible' }}>
            <h3 className="text-lg font-bold mb-4" style={{ color: BRAND_GREEN }}>{t.femSection}</h3>
            <EventDifferenceCardList 
              key={`diferenca-f-${allEvents.length}`}
              gender="F"
              allEvents={allEvents}
              allCompetitions={competitions}
              selectedYear={selectedYear}
            />
          </div>

          {/* Provas Masculinas */}
          <div style={{ overflow: 'visible', minHeight: '500px' }}>
            <h3 className="text-lg font-bold mb-4" style={{ color: BRAND_GREEN }}>{t.mascSection}</h3>
            <EventDifferenceCardList 
              key={`diferenca-m-${allEvents.length}`}
              gender="M"
              allEvents={allEvents}
              allCompetitions={competitions}
              selectedYear={selectedYear}
            />
          </div>
        </div>
      )}

    </div>
  );
}

// === COMPONENTE: LISTA DE CAMPEÕES POR COMPETIÇÃO ===
const ChampionsCardList = ({ gender, allEvents, allCompetitions, selectedYear, setSelectedYear }) => {
  const { lang } = useSportsLang();
  const t = SWIM_LABELS[lang] || SWIM_LABELS.pt;
  const [eventChampions, setEventChampions] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [availableYears, setAvailableYears] = React.useState([]);
  const scrollRef = React.useRef(null);
  
  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -400, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 400, behavior: 'smooth' });
    }
  };

  React.useEffect(() => {
    const fetchChampions = async () => {
      setLoading(true);
      try {
        // Buscar todos os campeões do gênero
        const response = await swimmingService.getChampions({ gender });
        const allChampions = response.data || [];
        
        console.log(' Total de campeões:', allChampions.length);
        
        // DEBUG: Verificar estrutura dos dados
        if (allChampions.length > 0) {
          console.log('📋 Exemplo de campeão:', allChampions[0]);
          console.log('📅 Anos disponíveis nos dados:', [...new Set(allChampions.map(c => c.year))].sort((a, b) => b - a));
          console.log(' Fases disponíveis:', [...new Set(allChampions.map(c => c.phase))]);
        }
        
        // Extrair anos únicos e ordenar
        const years = [...new Set(allChampions.map(c => c.year))].sort((a, b) => b - a);
        setAvailableYears(years);
        
        // Filtrar apenas campeões da Final principal (não Final B, C, D)
        // A API champions já retorna apenas 1º lugar, então só precisamos filtrar por fase e ano
        const champions = allChampions.filter(champ => {
          // Apenas Final (sem B, C, D - finais secundárias)
          const isFinalA = champ.phase === 'Final';
          const matchesYear = champ.year === selectedYear;
          
          return isFinalA && matchesYear;
        });
        
        console.log(` Campeões ${selectedYear}:`, champions.length);
        
        // Agrupar por PROVA (event_distance + event_stroke)
        const byEvent = {};
        champions.forEach(champ => {
          const eventKey = `${champ.event_distance}_${champ.event_stroke}`;
          if (!byEvent[eventKey]) {
            byEvent[eventKey] = {
              event_distance: champ.event_distance,
              event_stroke: champ.event_stroke,
              event_name: champ.event_name,
              champions: []
            };
          }
          byEvent[eventKey].champions.push(champ);
        });
        
        // Converter para array e ordenar por distância
        const eventsArray = Object.values(byEvent);
        eventsArray.sort((a, b) => a.event_distance - b.event_distance);
        
        setEventChampions(eventsArray);
      } catch (error) {
        console.error(' Erro ao buscar campeões:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchChampions();
  }, [gender, selectedYear]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-4" style={{ borderColor: BRAND_GREEN }}></div>
      </div>
    );
  }

  if (eventChampions.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        {t.noChampions(selectedYear)}
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Seta Esquerda */}
      <button
        onClick={scrollLeft}
        className="absolute left-0 top-1/2 -translate-y-1/2 z-10 text-white p-3 rounded-full transition-all duration-200 hover:bg-opacity-90 shadow-lg"
        style={{ backgroundColor: BRAND_GREEN }}
        aria-label="Scroll para esquerda"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      
      {/* Seta Direita */}
      <button
        onClick={scrollRight}
        className="absolute right-0 top-1/2 -translate-y-1/2 z-10 text-white p-3 rounded-full transition-all duration-200 hover:bg-opacity-90 shadow-lg"
        style={{ backgroundColor: BRAND_GREEN }}
        aria-label="Scroll para direita"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
      
      {/* Container dos cards */}
      <div 
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto pb-4 scroll-smooth px-12"
        style={{ scrollbarWidth: 'thin' }}
      >
        {eventChampions.map((event, index) => (
          <ChampionEventCard
            key={index}
            event={event}
          />
        ))}
      </div>
    </div>
  );
};

// === COMPONENTE: CARD DE PROVA COM CAMPEÕES (ESTILO GRÁFICO DE BARRAS) ===
const ChampionEventCard = ({ event }) => {
  if (!event.champions || event.champions.length === 0) return null;

  const champions = event.champions;
  
  // Calcular altura das barras baseado nos tempos
  const times = champions.map(c => parseFloat(c.final_time) || 0);
  const maxTime = Math.max(...times);
  const minTime = Math.min(...times);

  return (
    <div className="flex-shrink-0 bg-white rounded-xl shadow-md border border-gray-200 p-5" style={{ minWidth: '800px', maxWidth: '800px' }}>
      <div className="mb-4">
        <h4 className="font-bold text-xl" style={{ color: BRAND_GREEN }}>
          {event.event_distance}m {event.event_stroke}
        </h4>
      </div>

      <div className="space-y-5">
        {/* Gráfico de barras verticais */}
        <div className="flex items-end justify-start gap-3" style={{ height: '280px', width: '100%' }}>
          {champions.map((champion, index) => {
            const time = parseFloat(champion.final_time) || 0;
            const barHeight = maxTime > minTime 
              ? ((time - minTime) / (maxTime - minTime)) * 180 + 40
              : 100;
            const barColor = getGreenShade(index, champions.length);
            const clubLogo = getClubLogo(champion.club_name);
            
            return (
              <div key={index} className="flex flex-col items-center justify-end gap-2" style={{ flex: '1 1 0', minWidth: '28px' }}>
                {/* Nome do atleta + Bandeira/Logo + Tempo acima da barra */}
                <div className="flex flex-col items-center gap-1 mb-2">
                  {/* Nome do atleta (display_name) */}
                  <div className="text-xs font-bold text-center leading-tight" style={{ color: BRAND_GREEN }} title={champion.athlete_name || 'Atleta'}>
                    {champion.display_name || (champion.athlete_name || 'Atleta').split(' ')[0]}
                  </div>
                  
                  {/* Bandeira do país OU Logo do clube */}
                  {champion.nationality ? (
                    <img 
                      src={`/flags/${champion.nationality}.svg`}
                      alt={champion.nationality}
                      className="w-6 h-6 object-contain rounded"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  ) : clubLogo ? (
                    <img 
                      src={clubLogo} 
                      alt={champion.club_name || 'Clube'}
                      className="w-6 h-6 object-contain rounded"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  ) : null}
                  
                  {/* Tempo */}
                  <div className="text-sm font-bold text-center leading-none" style={{ color: BRAND_GREEN }}>
                    {typeof champion.final_time === 'number' ? formatSecondsToTime(champion.final_time) : champion.final_time}
                  </div>
                </div>
                
                {/* Barra vertical - TODA VERDE */}
                <div 
                  className="rounded-lg transition-all duration-300 relative group"
                  style={{
                    height: `${barHeight}px`,
                    backgroundColor: barColor,
                    minHeight: '40px',
                    width: '100%'
                  }}
                >
                  {/* Tooltip no hover */}
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block bg-gray-800 text-white text-sm rounded-lg py-2 px-3 whitespace-nowrap z-20 shadow-xl">
                    <div className="font-bold text-yellow-400">{champion.display_name || champion.athlete_name || 'Atleta'}</div>
                    {champion.nationality && (
                      <div className="text-blue-300 flex items-center gap-1">
                        <img 
                          src={`/flags/${champion.nationality}.svg`}
                          alt={champion.nationality}
                          className="w-4 h-4 object-contain inline"
                        />
                        {champion.nationality}
                      </div>
                    )}
                    <div className="text-gray-300">{champion.club_name || 'Sem clube'}</div>
                    <div className="text-green-300">{getCompetitionDisplayName(champion.competition_name)}</div>
                    <div className="text-yellow-300">{typeof champion.final_time === 'number' ? formatSecondsToTime(champion.final_time) : champion.final_time}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Legenda embaixo: Logo da competição + Nome */}
        <div className="flex items-center justify-start gap-3 mt-5">
          {champions.map((champion, index) => {
            const competitionLogo = getCompetitionLogo(champion.competition_name);
            const displayName = getCompetitionDisplayName(champion.competition_name);
            return (
              <div key={index} className="flex flex-col items-center text-center" style={{ flex: '1 1 0', minWidth: '28px' }}>
                {/* Logo da competição */}
                {competitionLogo && (
                  <img 
                    src={competitionLogo} 
                    alt={displayName}
                    className="w-7 h-7 object-contain rounded mb-1"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                )}
                {/* Nome da competição (sem ano) */}
                <div className="text-sm font-bold text-gray-900 leading-tight" title={displayName}>
                  {displayName}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// === COMPONENTE: LISTA DE CARDS DE EVENTOS (TEMPO MÉDIO) ===
const EventAverageCardList = ({ gender, allEvents, allCompetitions, selectedYear }) => {
  const { lang } = useSportsLang();
  const t = SWIM_LABELS[lang] || SWIM_LABELS.pt;
  const scrollRef = React.useRef(null);
  
  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -400, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 400, behavior: 'smooth' });
    }
  };
  
  // Filtrar eventos por gênero e EXCLUIR REVEZAMENTOS
  const eventsForGender = allEvents.filter(e => 
    e.gender === gender && 
    !e.is_relay && 
    e.is_relay !== true &&
    !e.name?.toLowerCase().includes('revezamento') &&
    !e.name?.toLowerCase().includes('relay')
  );
  
  console.log(` [Tempo Médio ${gender}] Total allEvents:`, allEvents.length);
  console.log(` [Tempo Médio ${gender}] Filtrados por gênero:`, eventsForGender.length);
  if (eventsForGender.length === 0) {
    console.warn(`⚠️ [Tempo Médio ${gender}] NENHUM EVENTO encontrado para gênero ${gender}!`);
  }
  
  // Agrupar por distância + estilo (remover duplicatas)
  const uniqueEvents = [];
  const seen = new Set();
  
  eventsForGender.forEach(event => {
    const key = `${event.distance_meters || event.distance}_${event.stroke}`;
    if (!seen.has(key)) {
      seen.add(key);
      uniqueEvents.push(event);
    }
  });

  // Ordenar por distância
  uniqueEvents.sort((a, b) => {
    const distA = a.distance_meters || a.distance || 0;
    const distB = b.distance_meters || b.distance || 0;
    return distA - distB;
  });

  console.log(` [Tempo Médio ${gender}] Eventos únicos após agrupamento:`, uniqueEvents.length);
  console.log(` [Tempo Médio ${gender}] Vai renderizar ${uniqueEvents.length} cards`);

  // Se não há eventos, mostrar mensagem
  if (uniqueEvents.length === 0) {
    console.error(` [Tempo Médio ${gender}] Renderizando mensagem de "sem dados"`);
    return (
      <div className="text-center py-8 text-gray-400" style={{ border: '3px solid red', padding: '20px' }}>
        <p style={{ fontSize: '20px', fontWeight: 'bold' }}>{t.noEvents(gender)}</p>
        <p>Total eventos recebidos: {allEvents.length}</p>
        <p>Eventos gênero {gender}: {eventsForGender.length}</p>
      </div>
    );
  }

  console.log(` [Tempo Médio ${gender}] Renderizando container com ${uniqueEvents.length} cards`);
  
  return (
    <div className="relative" style={{ overflow: 'visible', minHeight: '400px' }}>
      {/* Seta Esquerda */}
      <button
        onClick={scrollLeft}
        className="absolute left-0 top-1/2 -translate-y-1/2 z-10 text-white p-3 rounded-full transition-all duration-200 hover:bg-opacity-90 shadow-lg"
        style={{ backgroundColor: BRAND_GREEN }}
        aria-label="Scroll para esquerda"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      
      {/* Seta Direita */}
      <button
        onClick={scrollRight}
        className="absolute right-0 top-1/2 -translate-y-1/2 z-10 text-white p-3 rounded-full transition-all duration-200 hover:bg-opacity-90 shadow-lg"
        style={{ backgroundColor: BRAND_GREEN }}
        aria-label="Scroll para direita"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
      
      {/* Container dos cards */}
      <div 
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto pb-4 scroll-smooth px-12"
        style={{ scrollbarWidth: 'thin' }}
      >
        {uniqueEvents.map((event, index) => (
          <EventAverageCard
            key={index}
            event={event}
            gender={gender}
            allCompetitions={allCompetitions}
            selectedYear={selectedYear}
          />
        ))}
      </div>
    </div>
  );
};

// === COMPONENTE: LISTA DE CARDS DE EVENTOS (DIFERENÇA 1º vs 8º) ===
const EventDifferenceCardList = ({ gender, allEvents, allCompetitions, selectedYear }) => {
  const { lang } = useSportsLang();
  const t = SWIM_LABELS[lang] || SWIM_LABELS.pt;
  console.log(` [EventDifferenceCardList] Renderizando para ${gender}. allEvents:`, allEvents.length);
  
  const scrollRef = React.useRef(null);
  
  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -400, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 400, behavior: 'smooth' });
    }
  };
  
  // Filtrar eventos por gênero e EXCLUIR REVEZAMENTOS
  const eventsForGender = allEvents.filter(e => 
    e.gender === gender && 
    !e.is_relay && 
    e.is_relay !== true &&
    !e.name?.toLowerCase().includes('revezamento') &&
    !e.name?.toLowerCase().includes('relay')
  );
  
  console.log(` [Diferença ${gender}] Total allEvents:`, allEvents.length);
  console.log(` [Diferença ${gender}] Filtrados por gênero:`, eventsForGender.length);
  
  // Agrupar por distância + estilo (remover duplicatas)
  const uniqueEvents = [];
  const seen = new Set();
  
  eventsForGender.forEach(event => {
    const key = `${event.distance_meters || event.distance}_${event.stroke}`;
    if (!seen.has(key)) {
      seen.add(key);
      uniqueEvents.push(event);
    }
  });

  // Ordenar por distância
  uniqueEvents.sort((a, b) => {
    const distA = a.distance_meters || a.distance || 0;
    const distB = b.distance_meters || b.distance || 0;
    return distA - distB;
  });

  console.log(` [Diferença ${gender}] Eventos únicos após agrupamento:`, uniqueEvents.length);
  console.log(` [Diferença ${gender}] Vai renderizar ${uniqueEvents.length} cards`);

  // Se não há eventos, mostrar mensagem
  if (uniqueEvents.length === 0) {
    console.error(` [Diferença ${gender}] Renderizando mensagem de "sem dados"`);
    return (
      <div className="text-center py-8 text-gray-400" style={{ border: '3px solid red', padding: '20px' }}>
        <p style={{ fontSize: '20px', fontWeight: 'bold' }}>{t.noEvents(gender)}</p>
        <p>Total eventos recebidos: {allEvents.length}</p>
        <p>Eventos gênero {gender}: {eventsForGender.length}</p>
      </div>
    );
  }

  console.log(` [Diferença ${gender}] Renderizando container com ${uniqueEvents.length} cards`);

  return (
    <div className="relative" style={{ overflow: 'visible', minHeight: '400px' }}>
      {/* Seta Esquerda */}
      <button
        onClick={scrollLeft}
        className="absolute left-0 top-1/2 -translate-y-1/2 z-10 text-white p-3 rounded-full transition-all duration-200 hover:bg-opacity-90 shadow-lg"
        style={{ backgroundColor: BRAND_GREEN }}
        aria-label="Scroll para esquerda"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      
      {/* Seta Direita */}
      <button
        onClick={scrollRight}
        className="absolute right-0 top-1/2 -translate-y-1/2 z-10 text-white p-3 rounded-full transition-all duration-200 hover:bg-opacity-90 shadow-lg"
        style={{ backgroundColor: BRAND_GREEN }}
        aria-label="Scroll para direita"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
      
      {/* Container dos cards */}
      <div 
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto pb-4 scroll-smooth px-12"
        style={{ scrollbarWidth: 'thin' }}
      >
        {uniqueEvents.map((event, index) => (
          <EventDifferenceCard
            key={index}
            event={event}
            gender={gender}
            allCompetitions={allCompetitions}
            selectedYear={selectedYear}
          />
        ))}
      </div>
    </div>
  );
};

// === COMPONENTE: CARD DE EVENTO COM GRÁFICO VERTICAL (TODAS COMPETIÇÕES) ===
const EventAverageCard = ({ event, gender, allCompetitions, selectedYear }) => {
  const { lang } = useSportsLang();
  const t = SWIM_LABELS[lang] || SWIM_LABELS.pt;
  const [chartData, setChartData] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchAveragesForAllCompetitions = async () => {
      setLoading(true);
      
      try {
        // Usar a VIEW otimizada do Azure - TODAS as competições, APENAS FINAL
        const response = await swimmingService.getAvgTimesByPhase({
          event_id: event.id || event.event_id,
          gender: gender,
          phase: 'Final'  // Filtrar apenas Final principal (confirmado que tem dados)
        });
        
        const results = response.data || [];
        console.log(`📋 [Tempo Médio ${gender}] Total resultados:`, results.length);
        if (results.length > 0) {
          console.log(`📋 [Tempo Médio ${gender}] Exemplo:`, results[0]);
          console.log(`📋 [Tempo Médio ${gender}] Anos disponíveis:`, [...new Set(results.map(r => r.competition_year))]);
          const allPhases = [...new Set(results.map(r => r.phase))];
          console.log(`📋 [Tempo Médio ${gender}] Fases disponíveis:`, allPhases);
          console.log(`📋 [Tempo Médio ${gender}] Tem 'Final'?`, allPhases.includes('Final'));
          console.log(`📋 [Tempo Médio ${gender}] Dados com 'Final':`, results.filter(r => r.phase === 'Final').length);
        }
        
        // Transformar dados para o gráfico - APENAS FINAL PRINCIPAL e ANO SELECIONADO
        const data = results
          .filter(r => r.phase === 'Final' && r.competition_year === selectedYear)  // Apenas Final principal
          .map(r => ({
            competition: r.competition_name,
            year: r.competition_year,
            phase: r.phase,
            avgTime: r.avg_time_seconds,
            count: r.athlete_count,
            label: `${r.competition_name.substring(0, 15)} ${r.competition_year}`
          }));
        
        console.log(` [Tempo Médio ${gender}] Dados filtrados para ${selectedYear}:`, data.length);
        
        // Ordenar por competição
        data.sort((a, b) => a.competition.localeCompare(b.competition));
        
        setChartData(data);
      } catch (error) {
        console.error('Erro ao buscar médias:', error);
        setChartData([]);
      }
      
      setLoading(false);
    };
    
    fetchAveragesForAllCompetitions();
  }, [event, gender, allCompetitions, selectedYear]);

  const maxTime = chartData.length > 0 ? Math.max(...chartData.map(d => d.avgTime)) : 0;
  const minTime = chartData.length > 0 ? Math.min(...chartData.map(d => d.avgTime)) : 0;

  return (
    <div className="flex-shrink-0 bg-white rounded-xl shadow-md border border-gray-200 p-5" style={{ minWidth: '800px', maxWidth: '800px' }}>
      <div className="mb-4">
        <h4 className="font-bold text-xl" style={{ color: BRAND_GREEN }}>
          {event.distance_meters || event.distance}m {event.stroke}
        </h4>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2" style={{ borderColor: BRAND_GREEN }}></div>
        </div>
      ) : chartData.length === 0 ? (
        <p className="text-sm text-gray-400 py-12 text-center">{t.noData}</p>
      ) : (
        <div className="space-y-5">
          {/* Gráfico de barras verticais */}
          <div className="flex items-end justify-start gap-3" style={{ height: '240px', width: '100%' }}>
            {chartData.map((data, index) => {
              const barHeight = maxTime > minTime 
                ? ((data.avgTime - minTime) / (maxTime - minTime)) * 180 + 40
                : 100;
              const barColor = getGreenShade(index, chartData.length);
              
              return (
                <div key={index} className="flex flex-col items-center justify-end gap-2" style={{ flex: '1 1 0', minWidth: '28px' }}>
                  {/* Tempo acima da barra - GRANDE */}
                  <div className="text-base font-bold text-center leading-none mb-2" style={{ color: BRAND_GREEN }}>
                    {formatSecondsToTime(data.avgTime)}
                  </div>
                  
                  {/* Barra vertical SEM LOGO */}
                  <div 
                    className="rounded-lg transition-all duration-300 relative group"
                    style={{
                      height: `${barHeight}px`,
                      backgroundColor: barColor,
                      minHeight: '40px',
                      width: '100%'
                    }}
                  >
                    {/* Tooltip no hover */}
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block bg-gray-800 text-white text-sm rounded-lg py-2 px-3 whitespace-nowrap z-20 shadow-xl">
                      <div className="font-bold">{data.competition}</div>
                      <div className="text-gray-300">{data.phase}</div>
                      <div className="text-green-300">{data.count} atletas</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Legenda abaixo COM LOGO - BEM GRANDE */}
          <div className="flex items-center justify-start gap-3 mt-5">
            {chartData.map((data, index) => {
              const competitionLogo = getCompetitionLogo(data.competition);
              return (
                <div key={index} className="flex flex-col items-center text-center" style={{ flex: '1 1 0', minWidth: '28px' }}>
                  {/* Logo na legenda */}
                  {competitionLogo && (
                    <img 
                      src={competitionLogo} 
                      alt={data.competition}
                      className="w-7 h-7 object-contain rounded mb-1"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  )}
                  <div className="text-sm font-bold text-gray-900 leading-tight" title={`${data.competition} ${data.year}`}>
                    {data.competition.substring(0, 15)}
                  </div>
                  <div className="text-xs text-gray-700 leading-tight font-bold mt-0.5">{data.year}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// === COMPONENTE: CARD DE EVENTO COM DIFERENÇA 1º vs 8º (APENAS FINAIS - TODAS COMPETIÇÕES) ===
const EventDifferenceCard = ({ event, gender, allCompetitions, selectedYear }) => {
  const { lang } = useSportsLang();
  const t = SWIM_LABELS[lang] || SWIM_LABELS.pt;
  const [chartData, setChartData] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchDifferences = async () => {
      setLoading(true);
      
      try {
        // Usar a nova VIEW otimizada do Azure - TODAS as competições
        const response = await swimmingService.getFinalTimeDifferences({
          event_id: event.id || event.event_id,
          gender: gender,
          min_athletes: 8  // Garantir que tem pelo menos 8 atletas
          // Remover filtro phase para ver todas as fases disponíveis
        });
        
        const results = response.data || [];
        console.log(`📋 [Diferença ${gender}] Total resultados:`, results.length);
        if (results.length > 0) {
          console.log(`📋 [Diferença ${gender}] Exemplo:`, results[0]);
          console.log(`📋 [Diferença ${gender}] Anos disponíveis:`, [...new Set(results.map(r => r.competition_year))]);
          const allPhases = [...new Set(results.map(r => r.phase))];
          console.log(`📋 [Diferença ${gender}] Fases disponíveis:`, allPhases);
          console.log(`📋 [Diferença ${gender}] Tem 'Final'?`, allPhases.includes('Final'));
          console.log(`📋 [Diferença ${gender}] Dados com 'Final':`, results.filter(r => r.phase === 'Final').length);
        }
        
        // Transformar dados para o gráfico - APENAS FINAL PRINCIPAL e ANO SELECIONADO
        const data = results
          .filter(r => r.phase === 'Final' && r.competition_year === selectedYear)  // Apenas Final principal
          .map(r => ({
            competition: r.competition_name,
            year: r.competition_year,
            phase: r.phase,
            difference: Number(r.time_difference_seconds) || 0,
            differencePercent: Number(r.time_difference_percent) || 0,
            firstPlace: r.first_place_swimmer,
            eighthPlace: r.eighth_place_swimmer,
            athleteCount: r.total_athletes_in_final
          }));
        
        console.log(` [Diferença ${gender}] Dados filtrados para ${selectedYear}:`, data.length);
        
        // Ordenar por competição
        data.sort((a, b) => a.competition.localeCompare(b.competition));
        
        setChartData(data);
      } catch (error) {
        console.error('Erro ao buscar diferenças:', error);
        setChartData([]);
      }
      
      setLoading(false);
    };
    
    fetchDifferences();
  }, [event, gender, allCompetitions, selectedYear]);
  
  // Calcular min/max para normalização
  const differences = chartData.map(d => d.difference);
  const minDiff = Math.min(...differences, 0);
  const maxDiff = Math.max(...differences, 1);
  
  return (
    <div className="flex-shrink-0 bg-white rounded-xl shadow-md border border-gray-200 p-5" style={{ minWidth: '800px', maxWidth: '800px' }}>
      <h4 className="text-xl font-bold mb-4 truncate" style={{ color: BRAND_GREEN }}>
        {event.distance_meters || event.distance}m {event.stroke}
      </h4>
      
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2" style={{ borderColor: BRAND_GREEN }}></div>
        </div>
      ) : chartData.length === 0 ? (
        <p className="text-sm text-gray-400 py-12 text-center">{t.noFinalData}</p>
      ) : (
        <div className="space-y-5">
          {/* Gráfico de barras verticais */}
          <div className="flex items-end justify-start gap-3" style={{ height: '240px', width: '100%' }}>
            {chartData.map((data, index) => {
              const barHeight = maxDiff > minDiff 
                ? ((data.difference - minDiff) / (maxDiff - minDiff)) * 180 + 40
                : 100;
              const barColor = getGreenShade(index, chartData.length);
              
              return (
                <div key={index} className="flex flex-col items-center justify-end gap-2" style={{ flex: '1 1 0', minWidth: '28px' }}>
                  {/* Diferença acima da barra - GRANDE */}
                  <div className="text-base font-bold text-center leading-none mb-2" style={{ color: BRAND_GREEN }}>
                    +{data.difference.toFixed(2)}s
                  </div>
                  
                  {/* Barra vertical SEM LOGO */}
                  <div 
                    className="rounded-lg transition-all duration-300 relative group"
                    style={{
                      height: `${barHeight}px`,
                      backgroundColor: barColor,
                      minHeight: '40px',
                      width: '100%'
                    }}
                  >
                    {/* Tooltip no hover */}
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block bg-gray-800 text-white text-sm rounded-lg py-2 px-3 whitespace-nowrap z-20 shadow-xl">
                      <div className="font-bold">{data.competition}</div>
                      <div className="text-green-300">1º: {data.firstPlace}</div>
                      <div className="text-gray-300">8º: {data.eighthPlace}</div>
                      <div className="text-green-300">+{data.differencePercent.toFixed(1)}%</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Legenda abaixo COM LOGO - BEM GRANDE */}
          <div className="flex items-center justify-start gap-3 mt-5">
            {chartData.map((data, index) => {
              const competitionLogo = getCompetitionLogo(data.competition);
              return (
                <div key={index} className="flex flex-col items-center text-center" style={{ flex: '1 1 0', minWidth: '28px' }}>
                  {/* Logo na legenda */}
                  {competitionLogo && (
                    <img 
                      src={competitionLogo} 
                      alt={data.competition}
                      className="w-7 h-7 object-contain rounded mb-1"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  )}
                  <div className="text-sm font-bold text-gray-900 leading-tight" title={`${data.competition} ${data.year}`}>
                    {data.competition.substring(0, 15)}
                  </div>
                  <div className="text-xs text-gray-700 leading-tight font-bold mt-0.5">{data.year}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// === COMPONENTE DE GRÁFICO DE TEMPO MÉDIO POR FASE (ANTIGO - REMOVER) ===
const AverageTimeChart = ({ gender, selectedCompetition, selectedEvent, allEvents }) => {
  const [averages, setAverages] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  
  React.useEffect(() => {
    const fetchAveragesByPhase = async () => {
      if (!selectedEvent || !selectedCompetition) return;
      
      setLoading(true);
      console.log(' Buscando médias para:', gender, selectedEvent);
      
      // Encontrar o evento correspondente para o gênero desejado
      const targetGenderCode = gender === 'Masculino' ? 'M' : 'F';
      const eventForGender = allEvents.find(e => 
        (e.distance_meters || e.distance) === (selectedEvent.distance_meters || selectedEvent.distance) &&
        e.stroke === selectedEvent.stroke &&
        e.gender === targetGenderCode
      );
      
      if (!eventForGender) {
        console.log(' Evento não encontrado para', gender);
        setAverages([]);
        setLoading(false);
        return;
      }
      
      console.log(' Evento encontrado:', eventForGender);
      
      // Buscar resultados para cada fase
      const phases = ['Final', 'Final B', 'Semifinal', 'Eliminatórias'];
      const phaseData = [];
      
      for (const phase of phases) {
        try {
          const response = await swimmingService.getResults({
            competition_id: selectedCompetition.id,
            event_id: eventForGender.id || eventForGender.event_id,
            fase: phase
          });
          
          const results = response.results || [];
          console.log(`📊 ${phase} ${gender}:`, results.length, 'resultados');
          
          if (results.length > 0) {
            // Calcular tempo médio
            let totalTime = 0;
            let validCount = 0;
            
            results.forEach(result => {
              const timeInSeconds = parseTimeToSeconds(result.time_formatted || result.final_time || result.time_seconds);
              if (timeInSeconds > 0) {
                totalTime += timeInSeconds;
                validCount++;
              }
            });
            
            if (validCount > 0) {
              phaseData.push({
                phase,
                avgTime: totalTime / validCount,
                count: validCount
              });
            }
          }
        } catch (error) {
          console.error(`Erro ao buscar ${phase}:`, error);
        }
      }
      
      console.log('Dados finais:', phaseData);
      setAverages(phaseData);
      setLoading(false);
    };
    
    fetchAveragesByPhase();
  }, [gender, selectedCompetition, selectedEvent, allEvents]);
  
  // Componente não usado - removido
  return null;
};

// === FUNÇÕES AUXILIARES PARA CONVERSÃO DE TEMPO ===
const parseTimeToSeconds = (timeStr) => {
  if (typeof timeStr === 'number') return timeStr;
  if (!timeStr) return 0;
  
  const str = String(timeStr);
  
  // Formato MM:SS.MS ou M:SS.MS
  const match = str.match(/(\d+):(\d+)\.(\d+)/);
  if (match) {
    const minutes = parseInt(match[1]);
    const seconds = parseInt(match[2]);
    const milliseconds = parseInt(match[3]);
    return minutes * 60 + seconds + milliseconds / 100;
  }
  
  // Formato SS.MS
  const match2 = str.match(/(\d+)\.(\d+)/);
  if (match2) {
    const seconds = parseInt(match2[1]);
    const milliseconds = parseInt(match2[2]);
    return seconds + milliseconds / 100;
  }
  
  return parseFloat(str) || 0;
};

const formatSecondsToTime = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  const ms = Math.floor((seconds % 1) * 100); // TRUNCAR em vez de arredondar
  
  if (minutes > 0) {
    return `${minutes}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(2, '0')}`;
  }
  return `${secs}.${ms.toString().padStart(2, '0')}`;
};
